import { Entity, EntityResolved } from '@api';

import { WatchlistError } from '../../stores/use-watchlist/watchlist-types';

export const toEntityResolved = (entity: Entity): EntityResolved => ({
  ...entity,
  input: '',
});

export const buildRowSelectionFromTags = (
  tags: string[]
): Record<string, boolean> => Object.fromEntries(tags.map(tag => [tag, true]));

export const dedupeOrderedTags = (tags: string[]): string[] => {
  const seen = new Set<string>();
  return tags.filter(tag => {
    if (seen.has(tag)) {
      return false;
    }
    seen.add(tag);
    return true;
  });
};

export const tagsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((tag, index) => tag === b[index]);

// Tags leaving the filter that are not protected (manual chat/picker or edit prefill).
export const getRemovedFilterTagsNotProtected = (
  removedTags: readonly string[],
  protectedTags: ReadonlySet<string>
): string[] => removedTags.filter(tag => !protectedTags.has(tag));

export const prependEntitiesDedupedByTag = (
  newEntities: EntityResolved[],
  existing: EntityResolved[]
): EntityResolved[] => {
  const newByTag = new Map(newEntities.map(e => [e.tag, e]));
  return [...newByTag.values(), ...existing.filter(e => !newByTag.has(e.tag))];
};

export const mergeHydratedEntitiesIntoList = (
  prev: EntityResolved[],
  {
    hydrated,
    position = 'prepend',
  }: {
    hydrated: EntityResolved[];
    position?: 'prepend' | 'append';
  }
): EntityResolved[] => {
  const hydratedTagSet = new Set(hydrated.map(entity => entity.tag));
  const other = prev.filter(entity => !hydratedTagSet.has(entity.tag));

  if (position === 'append') {
    return [...other, ...hydrated];
  }

  return prependEntitiesDedupedByTag(hydrated, other);
};

interface ValidateListCreationProps {
  isAddMode: boolean;
  isEditMode: boolean;
  listName: string;
  rowSelection: Record<string, boolean>;
  unMappedEntitiesList: { input: string }[];
}

interface ValidationResult {
  isValid: boolean;
  tags: string[];
  unresolvedTags: string[];
  error?: WatchlistError;
}

export const validateListCreation = ({
  isAddMode,
  isEditMode,
  listName,
  rowSelection,
  unMappedEntitiesList,
}: ValidateListCreationProps): ValidationResult => {
  const isCreateMode = !isAddMode && !isEditMode;

  if (isCreateMode && !listName) {
    return {
      isValid: false,
      tags: [],
      unresolvedTags: [],
      error: WatchlistError.EmptyName,
    };
  }

  const tags = Object.keys(rowSelection).filter(tag => rowSelection[tag]);

  if ((isCreateMode || isEditMode) && !tags.length) {
    return {
      isValid: false,
      tags: [],
      unresolvedTags: [],
      error: WatchlistError.EmptyList,
    };
  }

  return {
    isValid: true,
    tags,
    unresolvedTags: unMappedEntitiesList.map(e => e.input),
  };
};
